import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { AlertTriangle, TrendingUp, Users, Wallet, Mail, ArrowUpRight, ArrowDownRight, Flame } from "lucide-react";
import { api, apiError, fmtDate } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import TutorialDayCard from "../components/TutorialDayCard";
import ActionsNow from "../components/ActionsNow";
import WhiteboardBanner from "../components/WhiteboardBanner";
import FundWidget from "../components/FundWidget";

const fmt = (n) => n == null ? "—" : Number(n).toLocaleString("en-IN", { maximumFractionDigits: 0 });

export default function CommandCentrePage() {
    const { user } = useAuth();
    const [data, setData] = useState(null);
    const [ar, setAr] = useState(null);
    const [bench, setBench] = useState(null);
    const [digestOn, setDigestOn] = useState(false);
    const [loading, setLoading] = useState(true);

    async function load() {
        setLoading(true);
        try {
            const [ov, dg, ar, bm] = await Promise.all([
                api.get("/command/overview"),
                api.get("/command/digest/me"),
                api.get("/ar-aging"),
                api.get("/command/benchmarking"),
            ]);
            setData(ov.data); setDigestOn(!!dg.data?.enabled);
            setAr(ar.data); setBench(bm.data);
        } catch (e) { toast.error(apiError(e)); }
        finally { setLoading(false); }
    }
    useEffect(() => { load(); }, []);

    async function toggleDigest() {
        try { const { data } = await api.patch("/command/digest/me", { enabled: !digestOn }); setDigestOn(data.enabled); toast.success(`Daily digest ${data.enabled ? "on" : "off"}`); }
        catch (e) { toast.error(apiError(e)); }
    }
    async function sendNow() {
        try { await api.post("/command/digest/send-now"); toast.success("Digest queued to outbox"); }
        catch (e) { toast.error(apiError(e)); }
    }

    if (loading || !data) return <div className="mx-auto max-w-7xl p-6"><div className="h-64 rounded-xl bg-white animate-pulse" /></div>;
    const c = data.money_strip.company;
    const marginDelta = c.margin_now - c.margin_prev;
    return (
        <div className="mx-auto max-w-7xl px-4 sm:px-6 py-6 sm:py-8" data-testid="command-centre">
            <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-3">
                <div>
                    <div className="text-xs font-medium uppercase tracking-wider text-gray-500">My page · {user?.effective_role}</div>
                    <h1 className="font-display text-2xl sm:text-3xl font-bold text-[#1E3355] mt-1">Command Centre</h1>
                    <div className="text-sm text-gray-500">MTD {data.month}</div>
                </div>
                <div className="flex items-center gap-2">
                    <button onClick={toggleDigest} className={`inline-flex items-center gap-1.5 text-sm font-medium px-3 py-1.5 rounded-md border ${digestOn ? "bg-[#1E3355] text-white border-[#1E3355]" : "bg-white text-[#1E3355] border-gray-200"}`} data-testid="digest-toggle">
                        <Mail className="w-4 h-4" /> Daily digest {digestOn ? "ON" : "OFF"}
                    </button>
                    {digestOn && <button onClick={sendNow} className="text-xs text-[#1E3355] hover:underline" data-testid="digest-send-now">Send now</button>}
                </div>
            </div>

            <TutorialDayCard />
            <WhiteboardBanner />
            <ActionsNow />

            {/* Issues board */}
            <section className="mt-6" data-testid="issues-board">
                <h2 className="font-display font-bold text-sm uppercase tracking-widest text-[#E02B2B] mb-2 inline-flex items-center gap-1.5">
                    <Flame className="w-4 h-4" /> Issues board · {data.issues_board.open_srs_count} open SRs · {data.issues_board.escalations_today.length} escalations today · {data.issues_board.absences_today.length} absences today
                </h2>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                    {data.issues_board.open_srs.slice(0, 6).map((sr) => (
                        <Link key={sr.id} to={`/service-requests`} className="rounded-xl bg-white border border-gray-100 fx-shadow-card p-3 hover:border-[#E02B2B]/50 transition-colors">
                            <div className="text-[10px] font-semibold uppercase tracking-widest text-[#E02B2B]">{sr.status}</div>
                            <div className="text-sm font-medium text-[#1E3355] mt-1">{sr.description?.slice(0, 60) || sr.category}</div>
                            <div className="text-[11px] text-gray-500 mt-1">{sr.site_name} · {fmtDate(sr.created_at)}</div>
                        </Link>
                    ))}
                    {data.issues_board.open_srs.length === 0 && (
                        <div className="text-sm text-gray-400 italic col-span-full">No open service requests. 🎉</div>
                    )}
                </div>
            </section>

            {/* Money strip */}
            <section className="mt-8" data-testid="money-strip">
                <h2 className="font-display font-bold text-sm uppercase tracking-widest text-[#1E3355] mb-2 inline-flex items-center gap-1.5">
                    <Wallet className="w-4 h-4" /> Money · company MTD
                </h2>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-3">
                    <Stat label="Income" value={c.income_now} sub={`prev ₹${fmt(c.income_prev)}`} />
                    <Stat label="Expenses" value={c.expenses_now} sub={`prev ₹${fmt(c.expenses_prev)}`} tone="expense" />
                    <Stat label="Margin" value={c.margin_now} sub={`Δ ₹${fmt(marginDelta)}`} tone={c.margin_now >= 0 ? "positive" : "negative"} />
                    <Stat label="Red-flagged sites" value={data.money_strip.rows.filter((r) => r.expense_over_20pct).length} sub=">20% over last month" tone={data.money_strip.rows.some((r) => r.expense_over_20pct) ? "negative" : "positive"} isRupee={false} />
                </div>
                <div className="rounded-xl bg-white border border-gray-100 fx-shadow-card overflow-x-auto">
                    <table className="w-full text-sm">
                        <thead className="bg-[#F4F6F9]">
                            <tr>
                                <th className="text-left p-2">Site</th>
                                <th className="text-right p-2">Income (MTD)</th>
                                <th className="text-right p-2">Expenses (MTD)</th>
                                <th className="text-right p-2">Margin</th>
                                <th className="text-center p-2">Type</th>
                            </tr>
                        </thead>
                        <tbody>
                            {data.money_strip.rows.map((r) => (
                                <tr key={r.site_id} className={`border-t border-gray-100 ${r.expense_over_20pct ? "bg-red-50" : ""}`}>
                                    <td className="p-2 font-medium text-[#1E3355]">{r.site_name}</td>
                                    <td className="p-2 text-right">₹{fmt(r.income_now)}</td>
                                    <td className="p-2 text-right">₹{fmt(r.expenses_now)}{r.expense_over_20pct && <span className="ml-1 text-[10px] font-bold text-[#E02B2B]">▲</span>}</td>
                                    <td className={`p-2 text-right font-semibold ${r.margin_now >= 0 ? "text-emerald-700" : "text-[#E02B2B]"}`}>₹{fmt(r.margin_now)}</td>
                                    <td className="p-2 text-center text-[10px] text-gray-500">{r.site_type}</td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            </section>

            {/* People pulse */}
            <section className="mt-8" data-testid="people-pulse">
                <h2 className="font-display font-bold text-sm uppercase tracking-widest text-[#1E3355] mb-2 inline-flex items-center gap-1.5">
                    <Users className="w-4 h-4" /> People pulse
                </h2>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                    <MiniList title="Top KRA" rows={data.people_pulse.top} colour="emerald" />
                    <MiniList title="Bottom KRA" rows={data.people_pulse.bottom} colour="red" />
                    <div className="rounded-xl bg-white border border-gray-100 fx-shadow-card p-3">
                        <div className="text-[11px] font-bold uppercase tracking-widest text-gray-500">Today's sign-offs</div>
                        <ul className="mt-2 space-y-1">
                            {data.people_pulse.signoff_today.slice(0, 6).map((s) => (
                                <li key={s.site_id} className="flex justify-between text-sm">
                                    <span className="truncate">{s.site_name}</span>
                                    <span className={s.signed_off ? "text-emerald-600 text-xs font-semibold" : "text-[#E02B2B] text-xs font-semibold"}>{s.signed_off ? "SIGNED" : "PENDING"}</span>
                                </li>
                            ))}
                            {data.people_pulse.signoff_today.length === 0 && <li className="text-xs text-gray-400 italic">No runs started today.</li>}
                        </ul>
                        <div className="mt-2 text-[11px] text-gray-500">Training overdue: <span className="font-semibold">{data.people_pulse.training_overdue}</span></div>
                    </div>
                </div>
            </section>

            {/* Trends */}
            <section className="mt-8" data-testid="trends-row">
                <h2 className="font-display font-bold text-sm uppercase tracking-widest text-[#1E3355] mb-2 inline-flex items-center gap-1.5">
                    <TrendingUp className="w-4 h-4" /> 6-month trends
                </h2>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                    <TrendPanel title="Margin" series={data.trends.margin_6mo} format="rupee" />
                    <TrendPanel title="Client rating" series={data.trends.ratings_6mo} format="rating" />
                    <IssuesTrend series={data.trends.issues_6mo} />
                </div>
            </section>
            {/* AR aging (billing sites) */}
            {ar && ar.sites?.length > 0 && (
                <section className="mt-8" data-testid="ar-aging-widget">
                    <h2 className="font-display font-bold text-sm uppercase tracking-widest text-[#1E3355] mb-2 inline-flex items-center gap-1.5">
                        <Wallet className="w-4 h-4" /> AR aging · outstanding invoices
                    </h2>
                    <div className="rounded-xl bg-white border border-gray-100 fx-shadow-card p-4">
                        <div className="grid grid-cols-4 gap-3 mb-4">
                            {["0-30", "31-60", "61-90", "90+"].map((b) => {
                                const val = ar.buckets?.[b] || 0;
                                const red = b === "61-90" || b === "90+";
                                return (
                                    <div key={b} className={`rounded-lg p-3 ${red && val > 0 ? "bg-red-50 border border-red-200" : "bg-gray-50 border border-gray-200"}`} data-testid={`ar-bucket-${b}`}>
                                        <div className="text-[10px] font-semibold uppercase text-gray-500">{b} days</div>
                                        <div className={`text-lg font-bold ${red && val > 0 ? "text-red-700" : "text-[#1E3355]"}`}>₹{fmt(val)}</div>
                                    </div>
                                );
                            })}
                        </div>
                        <table className="w-full text-sm">
                            <thead className="text-left text-xs text-gray-500 uppercase">
                                <tr><th className="py-1">Site</th><th className="py-1 text-right">Outstanding</th><th className="py-1 text-right">Invoices</th></tr>
                            </thead>
                            <tbody>
                                {ar.sites.map((s) => (
                                    <tr key={s.site_id} className="border-t border-gray-100">
                                        <td className="py-1.5 font-medium text-[#1E3355]">{s.site_name}</td>
                                        <td className="py-1.5 text-right font-semibold">₹{fmt(s.total)}</td>
                                        <td className="py-1.5 text-right text-xs text-gray-500">
                                            {s.invoices.map((i) => `${i.month} (${i.age_days}d)`).join(" · ")}
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                        <div className="mt-2 text-[11px] text-gray-500">Total outstanding: <span className="font-semibold text-[#1E3355]">₹{fmt(ar.total_outstanding_inr)}</span></div>
                    </div>
                </section>
            )}

            {/* Cross-site benchmarking */}
            {bench && bench.sites?.length > 0 && (
                <section className="mt-8" data-testid="benchmarking-widget">
                    <h2 className="font-display font-bold text-sm uppercase tracking-widest text-[#1E3355] mb-2 inline-flex items-center gap-1.5">
                        <TrendingUp className="w-4 h-4" /> Cross-site benchmarking (last 30 days)
                    </h2>
                    <div className="rounded-xl bg-white border border-gray-100 fx-shadow-card overflow-x-auto">
                        <table className="w-full text-sm">
                            <thead className="bg-gray-50 text-left text-xs text-gray-500 uppercase">
                                <tr>
                                    <th className="p-2">Site</th>
                                    <th className="p-2 text-right">Completion %</th>
                                    <th className="p-2 text-right">CRITICAL %</th>
                                    <th className="p-2 text-right">Sign-off hrs</th>
                                    <th className="p-2 text-right">Escalations</th>
                                    <th className="p-2 text-right">SR resp (d)</th>
                                    <th className="p-2 text-right">Attend. %</th>
                                </tr>
                            </thead>
                            <tbody>
                                {bench.sites.map((s, i) => {
                                    const best = i === 0;
                                    const worst = i === bench.sites.length - 1 && bench.sites.length > 2;
                                    return (
                                        <tr key={s.site_id} className={`border-t border-gray-100 ${best ? "bg-emerald-50/50" : worst ? "bg-red-50/40" : ""}`} data-testid={`bench-row-${s.site_id}`}>
                                            <td className="p-2 font-medium text-[#1E3355]">{s.site_name}</td>
                                            <td className="p-2 text-right">{s.completion_pct ?? "—"}%</td>
                                            <td className="p-2 text-right">{s.critical_pct ?? "—"}%</td>
                                            <td className="p-2 text-right">{s.avg_signoff_hours ?? "—"}</td>
                                            <td className="p-2 text-right">{s.escalations_30d}</td>
                                            <td className="p-2 text-right">{s.avg_sr_response_days ?? "—"}</td>
                                            <td className="p-2 text-right">{s.attendance_reliability_pct ?? "—"}%</td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                    </div>
                    <div className="mt-2 text-[11px] text-gray-500">Sorted by completion % · emerald = best · red = worst</div>
                </section>
            )}

            {/* Empowerment Fund — Owner/Head only widget (hides itself on 403) */}
            <FundWidget />
        </div>
    );
}

function Stat({ label, value, sub, tone, isRupee = true }) {
    const cls = {
        positive: "bg-emerald-50 border-emerald-200 text-emerald-800",
        negative: "bg-red-50 border-red-200 text-red-800",
        expense:  "bg-amber-50 border-amber-200 text-amber-800",
    }[tone] || "bg-white border-gray-200 text-[#1E3355]";
    return (
        <div className={`rounded-xl border p-3 ${cls}`}>
            <div className="text-[10px] font-medium uppercase tracking-wider opacity-70">{label}</div>
            <div className="font-display text-xl font-bold mt-1">{isRupee ? "₹" : ""}{fmt(value)}</div>
            {sub && <div className="text-[10px] mt-0.5 opacity-70">{sub}</div>}
        </div>
    );
}

function MiniList({ title, rows, colour }) {
    return (
        <div className="rounded-xl bg-white border border-gray-100 fx-shadow-card p-3">
            <div className="text-[11px] font-bold uppercase tracking-widest text-gray-500">{title}</div>
            <ul className="mt-2 space-y-1">
                {rows.map((r) => (
                    <li key={r.user_id} className="flex justify-between text-sm">
                        <span className="truncate">{r.user_name || "(no name)"}</span>
                        <span className={`text-xs font-semibold ${colour === "emerald" ? "text-emerald-700" : "text-[#E02B2B]"}`}>{r.overall}</span>
                    </li>
                ))}
                {rows.length === 0 && <li className="text-xs text-gray-400 italic">No people ranked yet.</li>}
            </ul>
        </div>
    );
}

function TrendPanel({ title, series, format }) {
    const values = series.map((s) => s.value ?? 0).filter((v) => v !== null);
    const max = Math.max(1, ...values.map((v) => Math.abs(v)));
    return (
        <div className="rounded-xl bg-white border border-gray-100 fx-shadow-card p-3">
            <div className="text-[11px] font-bold uppercase tracking-widest text-gray-500">{title}</div>
            <div className="mt-3 flex items-end gap-1 h-16">
                {series.map((s) => {
                    const v = s.value;
                    const h = v == null ? 4 : Math.max(4, Math.abs(v) / max * 60);
                    const colour = v == null ? "bg-gray-200" : v < 0 ? "bg-[#E02B2B]" : "bg-[#1E3355]";
                    return (
                        <div key={s.month} className="flex-1 flex flex-col items-center gap-1" title={`${s.month}: ${format === "rating" ? (v ?? "n/a") + "★" : "₹" + (v ?? 0)}`}>
                            <div className={`w-full rounded-t ${colour}`} style={{ height: `${h}px` }} />
                            <div className="text-[9px] text-gray-400">{s.month.slice(5)}</div>
                        </div>
                    );
                })}
            </div>
        </div>
    );
}

function IssuesTrend({ series }) {
    return (
        <div className="rounded-xl bg-white border border-gray-100 fx-shadow-card p-3">
            <div className="text-[11px] font-bold uppercase tracking-widest text-gray-500">Issues raised · resolved</div>
            <table className="w-full text-xs mt-2">
                <tbody>
                    {series.map((s) => (
                        <tr key={s.month} className="border-b border-gray-50">
                            <td className="py-0.5 text-gray-500">{s.month}</td>
                            <td className="py-0.5 text-right">{s.raised}<span className="text-emerald-600 ml-1">/{s.resolved}</span></td>
                        </tr>
                    ))}
                </tbody>
            </table>
        </div>
    );
}
